import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import * as H from "history";

import Grid from "src/library/components/Hedron";
import { BrandSplitView } from "src/library/components/BrandSplitView";
import { Text } from "src/library/components/Text";
import PoweredByMezze from "src/library/components/PoweredByMezze/PoweredByMezze";
import { Logo } from "src/core/shared/components/Logo";
import { useAuth } from "src/core/shared/api/auth";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import { PointOfContact } from "src/core/drops/api/drops.api";
import { SocialLoginButton } from "src/library/components/Button/SocialLoginButton";
import { SSOProviders } from "src/core/shared/store/global/global.reducer";
import { LegalPagesLinks } from "src/library/components/LegalPagesLinks";
import { ResetPasswordForm } from "src/core/auth/components/ResetPasswordForm";
import { LoginForm } from "src/core/auth/components/LoginForm";
import { MFAForm } from "src/core/auth/components/MFAForm";

import { LoginHelpModal } from "./LoginHelpModal";

/**
 * Main "Login" View
 *
 * @param {*} { user, getUserByPayroll, history }
 * @returns
 */
const LoginPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const [
    {
      login, confirmNewPassword, loginWithMicrosoft, loginWithGoogle, confirmMFACode
    },
    {
      user, loggingIn, error, socialLoggingIn, challengeName
    }
  ] = useAuth(true, props.history as H.History<{from?: string}>);

  const {
    currentUserStatus,
    globalPlatformSettings,
    globalPointOfContact,
    ssoSettings
  } = useMappedState(({ global }: IStore) => ({
    currentUserStatus: global.userSuspended,

    globalPlatformSettings: global.settings && global.settings.platform,
    globalPointOfContact: global.settings && global.settings.pointOfContact,
    ssoSettings: global.settings?.siteSettings?.sso
  }));

  const [ showLoginHelpModal, setShowLoginHelpModal ] = React.useState(false);

  const renderForm = React.useMemo(() => {
    if (challengeName === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      return (
        <ResetPasswordForm
          errorMessage={error}
          loggingIn={loggingIn}
          onSubmit={newPassword => confirmNewPassword(user, newPassword)}
        />
      );
    } else if (challengeName === "CONFIRM_SIGN_IN_WITH_SMS_CODE" || challengeName === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
      return (
        <MFAForm
          errorMessage={error}
          loggingIn={loggingIn}
          mfaType={challengeName}
          onSubmit={mfaCode => confirmMFACode(mfaCode)}
        />
      );
    } else {
      return null; // or return a default value
    }
  }, [
    challengeName,
    confirmMFACode,
    confirmNewPassword,
    error,
    loggingIn,
    user
  ]);

  return (
    <BrandSplitView currentUserStatus={currentUserStatus}>
      <AuthInner direction="vertical">
        <Grid.Box className="logo-wrapper">
          <StyledDarkLogo />
        </Grid.Box>

        <Grid.Box>
          <Text
            fontSize="h4"
            color="secondary"
          >
            Welcome. Please login to your account.
          </Text>
        </Grid.Box>

        {globalPlatformSettings && globalPlatformSettings.maintenance_mode.roles.length > 0 && (
          <Grid.Box style={{ paddingTop: "14px" }}>
            <Text
              fontSize="h4"
              color="secondary"
            >
              System in maintenance mode
            </Text>
          </Grid.Box>
        )}

        <AuthFormWrapper>
          {challengeName !== undefined ? renderForm :
          (
            <LoginWrapper>
              {!socialLoggingIn ? (
                <LoginForm
                  errorMessage={error}
                  loggingIn={loggingIn}
                  onSubmit={(username, password) => login(username, password)}
                />
              ) : (
                <div className="completing-social-login">
                  <Text>
                    Completing login...
                  </Text>
                </div>
              )}

              <div className="social-login-links">
                {ssoSettings?.providers.map((provider, i) =>
                  (provider.type === SSOProviders.Google ? (
                    <SocialLoginButton
                      key={`${provider.type}-${i}`}
                      socialType={SSOProviders.Google}
                      onClick={() => loginWithGoogle()}
                    >
                      Login With Google
                    </SocialLoginButton>
                  ) : (
                    <SocialLoginButton
                      key={`${provider.type}-${i}`}
                      socialType={SSOProviders.Microsoft}
                      onClick={() => loginWithMicrosoft(provider.providerName)}
                    >
                      Login With Microsoft
                    </SocialLoginButton>
                  ))
                )}

              </div>

              {globalPointOfContact && (

                <LoginHelpWrapper>
                  <Text
                    className="login-help-text"
                    onClick={() => {
                      setShowLoginHelpModal(true);
                    }}
                  >
                    Need help logging in?
                  </Text>

                  <LoginHelpModal
                    onDismiss={() => setShowLoginHelpModal(false)}
                    open={showLoginHelpModal}
                    contactDetails={globalPointOfContact as PointOfContact}
                  />
                </LoginHelpWrapper>
              )}
            </LoginWrapper>
          )}
        </AuthFormWrapper>

        <AuthFooter direction="vertical">
          <PoweredByMezze type="light" />

          <LegalPagesLinks />

        </AuthFooter>
      </AuthInner>
    </BrandSplitView>
  );
};

const StyledDarkLogo = styled(Logo).attrs({ type: "dark" })`
  margin-bottom: 30px;
`;

export const AuthFormWrapper = styled(Grid.Box)`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.margins.xl};

  form {
    width: 100%;
    max-width: 400px;
  }
`;

export const AuthFooter = styled(Grid.Bounds)`
  margin-top: ${({ theme }) => theme.spacing.margins.lg};
  width: 100%;
  align-items: center;
`;

export const AuthInner = styled(Grid.Bounds)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  .logo-wrapper {
    img {
      max-width: 300px;
      max-height: 120px;
    }
  }
  ${({ theme }) =>
    css`
      padding: ${theme.spacing.padding.xl};
    `};
`;

const LoginHelpWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    justify-content: center;
    padding: ${theme.spacing.padding.md};

    .login-help-text {
      color: ${theme.colors.primaryDark};
      text-decoration: underline;
      cursor: pointer;
    }
  `}
`;

const LoginWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;

    .completing-social-login {
      text-align: center;
    }
    .social-login-links {
      display: flex;
      flex-direction: column;
      align-items: center;

      .button {
        margin-top: ${theme.spacing.margins.md};
      }
    }
  `}
`;

export default LoginPage;
